import { apiClient } from '../apiClient';

// Upload single file
export const uploadSingleFile = async (file, onProgress = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    const response = await apiClient.post('/upload/single', formData, config);
    return response.data;
  } catch (error) {
    console.error('Upload single file error:', error);
    throw new Error(error.response?.data?.message || 'Failed to upload file. Please try again.');
  }
};

// Upload multiple files
export const uploadMultipleFiles = async (files, onProgress = null) => {
  try {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file);
    });

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    const response = await apiClient.post('/upload/multiple', formData, config);
    return response.data;
  } catch (error) {
    console.error('Upload multiple files error:', error);
    throw new Error(error.response?.data?.message || 'Failed to upload files. Please try again.');
  }
};

// Delete file
export const deleteFile = async (publicId) => {
  try {
    const response = await apiClient.delete(`/upload/${publicId}`);
    return response.data;
  } catch (error) {
    console.error('Delete file error:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete file. Please try again.');
  }
};

// Get file info
export const getFileInfo = async (publicId) => {
  try {
    const response = await apiClient.get(`/upload/${publicId}`);
    return response.data;
  } catch (error) {
    console.error('Get file info error:', error);
    throw new Error(error.response?.data?.message || 'Failed to get file info. Please try again.');
  }
};

// Validate file before upload
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.pdf'],
  } = options;

  const errors = [];

  // Check file size
  if (file.size > maxSize) {
    errors.push(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    errors.push(`File type not supported. Allowed types: ${allowedTypes.join(', ')}`);
  }

  // Check file extension
  const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  if (!allowedExtensions.includes(fileExtension)) {
    errors.push(
      `File extension not supported. Allowed extensions: ${allowedExtensions.join(', ')}`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Upload file with validation and progress
export const uploadFileWithValidation = async (file, options = {}, onProgress = null) => {
  // Validate file first
  const validation = validateFile(file, options);
  if (!validation.isValid) {
    throw new Error(validation.errors.join(', '));
  }

  // Upload file
  return await uploadSingleFile(file, onProgress);
};
